/**
 * Device capability detection.
 *
 * Shared signals used across map / aircraft / particle layers to tune
 * down work on low-end Android (Snapdragon 6xx, 4GB RAM) and on devices
 * the user has set to data-saver / reduced-motion.
 *
 * Values are cached after first read because the underlying APIs don't
 * change at runtime (no point re-checking every render).
 */

let _isLowEnd: boolean | null = null;
let _prefersReducedMotion: boolean | null = null;

interface NavigatorWithLowEndHints extends Navigator {
  deviceMemory?: number;
  connection?: {
    saveData?: boolean;
    effectiveType?: string;
  };
}

/**
 * Returns true if the device looks like an entry-level Android (or any
 * device where we should aggressively cap CPU/GPU work):
 *
 *   - <4 GB navigator.deviceMemory, OR
 *   - hardwareConcurrency ≤ 4 cores, OR
 *   - navigator.connection.saveData === true, OR
 *   - effectiveType is 'slow-2g' / '2g'
 *
 * Conservative by design — false negatives are fine (mid-range Android
 * runs the full experience), but a false positive on a flagship is
 * imperceptible (slight resolution drop / fewer particles).
 */
export function isLowEndDevice(): boolean {
  if (_isLowEnd !== null) return _isLowEnd;
  if (typeof navigator === 'undefined') {
    _isLowEnd = false;
    return _isLowEnd;
  }
  const nav = navigator as NavigatorWithLowEndHints;
  const lowMem = (nav.deviceMemory ?? 8) < 4;
  const lowCores = (navigator.hardwareConcurrency ?? 8) <= 4;
  const conn = nav.connection;
  const saveData = conn?.saveData === true;
  const slowConn = conn?.effectiveType === 'slow-2g' || conn?.effectiveType === '2g';
  _isLowEnd = lowMem || lowCores || saveData || slowConn;
  return _isLowEnd;
}

/** Returns true if the user has prefers-reduced-motion set. */
export function prefersReducedMotion(): boolean {
  if (_prefersReducedMotion !== null) return _prefersReducedMotion;
  if (typeof window === 'undefined' || !window.matchMedia) {
    _prefersReducedMotion = false;
    return _prefersReducedMotion;
  }
  _prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return _prefersReducedMotion;
}

/** Reset the cached values. Test-only — production callers shouldn't need this. */
export function _resetDeviceCacheForTests(): void {
  _isLowEnd = null;
  _prefersReducedMotion = null;
}
